import { SetStateAction, useEffect, useState, Dispatch } from 'react'

type SetValue<T> = Dispatch<SetStateAction<T>>

function useLocalStorageState<T>(
  key: string,
  initialValue: T,
): [T, SetValue<T>] {
  // Initialize state with the value from localStorage or the initialValue
  const [state, setState] = useState<T>(() => {
    const storedValue = localStorage.getItem(key)
    return storedValue !== null ? JSON.parse(storedValue) : initialValue
  })

  // Update localStorage when state changes
  useEffect(() => {
    localStorage.setItem(key, JSON.stringify(state))
  }, [key, state])

  return [
    state,
    (newState: SetStateAction<T>) => {
      setState((prevState) => {
        const newStateValue =
          typeof newState === 'function'
            ? (newState as Function)(prevState)
            : newState

        // Handle property deletions by removing undefined values
        const updatedState = { ...prevState, ...newStateValue }
        for (const key in updatedState) {
          if (updatedState[key] === undefined) {
            delete updatedState[key]
          }
        }
        return updatedState
      })
    },
  ]
}

export default useLocalStorageState
